import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Footprints } from "lucide-react";

export function VestigioConfigPanel() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(
    () => api.get("/electric-rooms/vestigio-types", { params: { include_inactive: true } })
      .then(({ data }) => setItems(data)).catch(() => {}),
    []
  );
  useEffect(() => { load(); }, [load]);

  const call = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Card className="p-5 border-border bg-card space-y-4" data-testid="vestigios-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
            <Footprints size={15} className="text-orange-400" /> Vestígios — Salas Elétricas ({items.length})
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Opções de vestígios disponíveis no monitoramento (marcas de roedura, trilhas, ninhos etc.). Alterações refletem automaticamente no ERP e no aplicativo.
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs"
          onClick={() => setDialog({ name: "", requires_detail: false })} data-testid="add-vestigio-btn">
          <Plus size={13} className="mr-1" /> Adicionar Vestígio
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">Nenhum vestígio cadastrado.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {items.map((v) => (
            <div key={v.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card ${!v.active ? "opacity-50" : ""}`} data-testid={`vestigio-${v.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{v.name}</p>
                {v.requires_detail && <p className="text-[10px] text-muted-foreground">Exige descrição do técnico</p>}
              </div>
              {!v.active && <Badge variant="outline" className="text-[9px] shrink-0">inativo</Badge>}
              <div className="flex items-center gap-1 shrink-0">
                <Switch checked={!!v.active} onCheckedChange={() => call(() => api.patch(`/electric-rooms/vestigio-types/${v.id}/toggle`))} className="scale-[0.65]" data-testid={`vestigio-toggle-${v.id}`} />
                <button className="text-muted-foreground hover:text-primary p-0.5" onClick={() => setDialog({ ...v })} title="Editar" data-testid={`vestigio-edit-${v.id}`}><Pencil size={13} /></button>
                <button className="text-muted-foreground hover:text-destructive p-0.5" title="Excluir" data-testid={`vestigio-delete-${v.id}`}
                  onClick={() => { if (window.confirm(`Excluir o vestígio "${v.name}"?`)) call(() => api.delete(`/electric-rooms/vestigio-types/${v.id}`), "Vestígio excluído"); }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="vestigio-dialog">
          <DialogHeader><DialogTitle className="font-heading">{dialog?.id ? "Editar Vestígio" : "Novo Vestígio"}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nome do vestígio</Label>
                <Input value={dialog.name} placeholder="Ex.: Marcas de roedura"
                  onChange={(e) => setDialog({ ...dialog, name: e.target.value })} data-testid="vestigio-name-input" /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={!!dialog.requires_detail}
                  onCheckedChange={(v) => setDialog({ ...dialog, requires_detail: !!v })}
                  data-testid="vestigio-requires-detail" />
                Exigir descrição livre do técnico (ex.: opção "Outros")
              </label>
              <Button className="w-full" data-testid="vestigio-save-btn" onClick={() => {
                if (!dialog.name.trim()) return toast.error("Nome do vestígio obrigatório");
                const body = { name: dialog.name, requires_detail: !!dialog.requires_detail };
                call(() => dialog.id ? api.put(`/electric-rooms/vestigio-types/${dialog.id}`, body) : api.post("/electric-rooms/vestigio-types", body),
                  dialog.id ? "Vestígio atualizado" : "Vestígio cadastrado");
                setDialog(null);
              }}>Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
